import { Button, DocumentCheckbox, Header, LoadingSpinner } from '../Generic'
import CryptoJS from 'crypto-js'
import { observer } from 'mobx-react'
import type { CSSProperties } from 'react'
import { useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { actionReport } from '@methods/actionReport'
import { axiosInstance } from '@methods/axiosConfig'
import { devLog } from '@methods/devLog'

import { Consent, ConsentsRes, HeaderConfiguration } from '@/types/acceptanceDocument'
import { forceRedirect } from '@methods/forceRedirect'
import { API_URL } from '@constants/apiUrl'
import { cx } from '@/utils/cx'

export interface AdditionalParagraphProps {
  tabIndex?: number
  theme?: Record<string, string>
  trans?: Record<string, string>
  data?: Record<string, unknown>
}

interface TermsAndConditionsProps {
  showHeader?: boolean
  header?: string
  AdditionalParagraph?: React.FC<AdditionalParagraphProps>
  additionalParagraphProps?: AdditionalParagraphProps
  showTitle?: boolean
  showRequired?: boolean
  buttonText?: string
}

export const TermsAndConditions = observer(
  ({
    showHeader = true,
    header,
    AdditionalParagraph,
    additionalParagraphProps,
    showTitle = true,
    showRequired = true,
    buttonText,
  }: TermsAndConditionsProps) => {
    const store = useContext(StoreContext)

    const { themeId } = store.InterfaceState
    const { currentScenarioId } = store.ScenarioState
    const { generalConditionsPage: trans } = store.TranslationsState.translations

    const [headerConfiguration, setHeaderConfiguration] = useState<HeaderConfiguration | null>()
    // Checkboxes state
    const [consents, setConsents] = useState<Consent[]>([])
    const [requiredConsents, setRequiredConsents] = useState([])
    const [acceptedConsents, setAcceptedConsents] = useState(new Set())
    const [proceedDisabled, setProceedDisabled] = useState(true)
    const [proceedOngoing, setProceedOngoing] = useState(false)

    // Loadings state
    const [loading, setLoading] = useState(true)
    const [btnLoading, setBtnLoading] = useState(false)

    // Mobile utilities
    const [mobileContainerHeight, setMobileContainerHeight] = useState('')

    const { isMobile } = store.AppState
    const { width, height } = store.AppState.screenSize
    useEffect(() => {
      setMobileContainerHeight(`calc(${window.innerHeight}px - 120px)`)
    }, [width, height])

    const getDocuments = async () => {
      try {
        const res = await axiosInstance.get<ConsentsRes>(
          `${API_URL}/terms-and-conditions/consents`,
          { withCredentials: true }
        )

        res.data.headerConfiguration && setHeaderConfiguration(res.data.headerConfiguration)
        setConsents(res.data.consents)

        res.data.consents.map((consent) => {
          consent.documents.map((document) => {
            actionReport({
              type: 'event.onboarding-web.consent.CONSENT_PRESENTED',
              payload: {
                serviceType: consent.serviceType,
                documentType: document.documentType,
                isRequired: !document.optional,
              },
            })
          })
        })

        const requiredConsents = res.data.consents.reduce((acc1, consent) => {
          const docs = consent.documents.reduce((acc: any, document) => {
            if (!document.optional) {
              const documentId = `${consent.serviceType}_${document.documentType}`

              acc.push(documentId)
            }

            return acc
          }, [])

          return acc1.concat(docs)
        }, [])

        setRequiredConsents(requiredConsents)
        setProceedDisabled(requiredConsents.length > 0)
        setLoading(false)
      } catch (e) {
        devLog(e)
      }
    }

    const handleProceed = async () => {
      if (![...requiredConsents].every((val) => acceptedConsents.has(val))) return

      try {
        if (currentScenarioId !== 'localTests' && !proceedOngoing) {
          setBtnLoading(true)
          setProceedOngoing(true)
          const tempConsents = consents.map((consent) => {
            const tempDocuments = consent.documents.map(({ file, ...rest }) => {
              return {
                ...rest,
                digest: CryptoJS.SHA256(CryptoJS.enc.Base64.parse(file)).toString(CryptoJS.enc.Hex),
                accepted: acceptedConsents.has(`${consent.serviceType}_${rest.documentType}`),
              }
            })

            return {
              serviceType: consent.serviceType,
              documents: tempDocuments,
            }
          })

          await axiosInstance.post(
            `${API_URL}/terms-and-conditions/consents/accept`,
            {
              consents: tempConsents,
            },
            {
              withCredentials: true,
            }
          )

          forceRedirect(`${API_URL}/terms-and-conditions/complete`)
        }
      } catch (e) {
        setBtnLoading(false)
        setProceedOngoing(false)
        devLog(e)
      }
    }

    const handleConsent = ({
      checked,
      id,
      isRequired,
      documentType,
      serviceType,
    }: {
      checked: boolean
      id: string
      isRequired: boolean
      documentType: string
      serviceType: string
    }) => {
      const currentAcceptedConsents = acceptedConsents

      if (checked) {
        currentAcceptedConsents.add(id)
        actionReport({
          type: 'event.onboarding-web.consent.CONSENT_CHECKED',
          payload: {
            serviceType,
            documentType,
            isRequired,
          },
        })
      } else {
        currentAcceptedConsents.delete(id)
        actionReport({
          type: 'event.onboarding-web.consent.CONSENT_UNCHECKED',
          payload: {
            serviceType,
            documentType,
            isRequired,
          },
        })
      }

      setAcceptedConsents(currentAcceptedConsents)

      ![...requiredConsents].every((val) => currentAcceptedConsents.has(val))

      setProceedDisabled(![...requiredConsents].every((val) => currentAcceptedConsents.has(val)))
    }

    useEffect(() => {
      if (window.location.pathname.split('/')[1] !== 'mock') {
        getDocuments()
      }
    }, [])

    const partiallyResetState = () => {
      setBtnLoading(false)
      setProceedOngoing(false)
    }

    useEffect(() => {
      window.addEventListener('pageshow', partiallyResetState)

      return () => {
        window.removeEventListener('pageshow', partiallyResetState)
      }
    }, [])

    return (
      <div className="page-container">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div
              className="layout__content-container-with-scroll"
              style={
                isMobile
                  ? ({ '--ccws-mobile-height': mobileContainerHeight } as CSSProperties)
                  : undefined
              }
            >
              {showHeader && (
                <Header>
                  {headerConfiguration && (
                    <p className="generic__paragraph-centered">
                      {headerConfiguration.title?.default}
                    </p>
                  )}
                  {header || trans.header}
                </Header>
              )}

              {AdditionalParagraph && <AdditionalParagraph {...additionalParagraphProps} />}

              {consents &&
                consents.map((consent) =>
                  consent.documents.map(
                    (document) =>
                      !document.optional && (
                        <DocumentCheckbox
                          title={document.title && showTitle ? document.title : undefined}
                          showAsterisk={(!document.optional && showRequired) || undefined}
                          inputName={`${consent.serviceType}_${document.documentType}`}
                          description={document.label}
                          externalUrl={document.fileUrl}
                          handleDecision={handleConsent}
                          value={document.accepted || false}
                          documentType={document.documentType}
                          serviceType={consent.serviceType}
                          isRequired={!document.optional}
                          goToDocumentLabel={trans.goToDocument}
                        />
                      )
                  )
                )}

              {consents &&
                consents.map((consent) =>
                  consent.documents.map(
                    (document) =>
                      document.optional && (
                        <DocumentCheckbox
                          title={document.title && showTitle ? document.title : undefined}
                          showAsterisk={(!document.optional && showRequired) || undefined}
                          inputName={`${consent.serviceType}_${document.documentType}`}
                          description={document.label}
                          externalUrl={document.fileUrl}
                          handleDecision={handleConsent}
                          value={document.accepted || false}
                          documentType={document.documentType}
                          serviceType={consent.serviceType}
                          isRequired={!document.optional}
                          goToDocumentLabel={trans.goToDocument}
                        />
                      )
                  )
                )}
              {showRequired && (
                <div className="generic__required-footer generic__required-footer--no-top-margin">
                  {trans.required}
                </div>
              )}
            </div>

            <div className="layout__content-container-bottom-fixed">
              {!isMobile ? <div className="layout__content-container-bottom-left" /> : <div />}
              <Button onClick={handleProceed} disabled={proceedDisabled} id="page-submit">
                {btnLoading && <LoadingSpinner inButton />}
                <span
                  className={cx(
                    'generic__button-text',
                    btnLoading && 'generic__button-text--loading'
                  )}
                >
                  {buttonText ?? trans.proceed}
                </span>
              </Button>
            </div>
            <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
          </>
        )}
      </div>
    )
  }
)
